import {
    ArrowDropDown,
    ArrowDropUp,
} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import InfoIcon from '@mui/icons-material/Info';
import More from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopCircle from '@mui/icons-material/StopCircle';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {
    Button,
    Checkbox,
    Hidden,
    IconButton,
    MenuItem,
    Skeleton,
    styled,
    TableBody as MatTableBody,
    TableCell as MatTableCell,
    TableContainer as MatTableContainer,
    TableHead as MatTableHead,
    TableRow as MatTableRow,
    Tooltip,
} from '@mui/material';
import type { TableOwnProps } from '@mui/material/Table';
import type {
    ColumnDef,
    ColumnFiltersState,
    Row,
} from '@tanstack/react-table';
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { t } from 'i18next';
import type { ReactElement } from 'react';
import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Key } from 'ts-keycode-enum';
import { v4 as uuid } from 'uuid';

import getPageSize from '../../../config/Pagination';
import { BaseTableIcons } from '../../../enums/BaseTableIcons';
import { bindKeyTo } from '../../../utils/a11y';
import type { Icon } from '../../../utils/IconsInterface';
import { WithVisibility } from '../../WithVisibility';
import type { PaginationRecordsPerPage } from '../Pagination/Pagination';
import Pagination, { PaginationChangeActionType } from '../Pagination/Pagination';
import {
    BodyText,
    ColumnTitle,
} from '../Typography';
import {
    StyledActionRow,
    StyledContextPopupMenu,
    StyledTable,
    StyledTableRow,
    StyledTableSelectionContainer,
} from './BaseTableStyledComponents';
import type {
    HeaderDefinition,
    HeaderGroupDefinition,
} from './types';

interface ContextMenuItem {
    text: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    click?: (event: React.MouseEvent<HTMLLIElement>, data: any, selectedData?: any) => void;
    type?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    disable?: ((row: any) => boolean) | boolean;
}

export interface SelectionItem {
    text: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    click: (data: any) => void;
}

interface Conditional {
    visible?: boolean;
}

interface TableProps {
    columns: Array<ColumnDef<any>>;
    showRefresh: boolean;
    defaultSortBy?: string;
    defaultSortOrder?: string;
    data: Array<{}>;
    size?: TableOwnProps['size'];
    total: number;
    onPagination?: Function;
    isLoading: boolean;
    contextMenuItems?: ContextMenuItem[];
    isSelectAble?: boolean;
    icons?: Icon[];
    tooltip?: string;
    refreshButtonClicked: () => void;
    selectionItems?: SelectionItem;
    isfiltersVisible?: boolean;
    empty?: boolean;
    canNextPageProp?: boolean;
    // todo: Sahil to fix this
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onTableCellClicked?(row: any, event: any): any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onRowHoverTooltip?: ((row: any) => string) | string;
    onItemSelection?: any;
    entityName?: string;
    keyColumnIndex?: number;
    level?: string;
}

interface TableHeadersProps {
    hasActionColumn?: boolean;
    hasRefreshButton?: boolean;
    headerGroups: HeaderGroupDefinition[];
    gotoPage: Function;
    isSelectAble?: boolean;
    isfiltersVisible?: boolean;
    entityName?: string;
    refreshButtonClicked?: () => void;
    toggleSortBy: (ColumnId: string, descending?: boolean, isMulti?: boolean) => void;
}

export interface PaginationArgs {
    pageIndex: number;
    pageSize: number;
    sortBy?: string;
    sortOrder?: string;
    pageCount: number;
    canNextPage: boolean;
    canPreviousPage: boolean;
    sortDirection: 'ASC' | 'DESC' | null;
    filters?: FiltersObj[];
}

export interface FiltersObj {
    id: string;
    value: {
        apiField: string;
        value: string;
    };
}

const HeaderRefreshBtnContainer = styled('th')({
    height: '52px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: '16px',
});

interface EmptyContentProps {
    empty?: boolean;
    colspan: number;
    text: string;
}

const EmptyContent: React.FC<EmptyContentProps> = ({
    empty, colspan, text,
}) => (
    empty ? (
        <MatTableRow >
            <MatTableCell
                colSpan={colspan}
                data-testid="table-no-data"
                data-cy="table-no-data">
                <BodyText>
                    {text}
                </BodyText>
            </MatTableCell>
        </MatTableRow>
    ) : null
);

const CustomTable: React.FC<TableProps> = (
    {
        columns,
        data,
        size,
        contextMenuItems,
        isSelectAble,
        onPagination,
        isLoading,
        total,
        icons,
        onTableCellClicked,
        showRefresh,
        refreshButtonClicked,
        selectionItems,
        empty,
        canNextPageProp,
        defaultSortBy,
        defaultSortOrder,
        isfiltersVisible,
        onRowHoverTooltip,
        onItemSelection,
        entityName = '',
        keyColumnIndex,
        level = '',
    }) => {

    const [ filters, setFilters ] = useState<ColumnFiltersState>([]);
    const sortParams = [];
    if (defaultSortBy && defaultSortOrder) {
        sortParams.push({
            id: defaultSortBy,
            desc: defaultSortOrder === 'DESC' || defaultSortOrder === 'desc',
        });
    }

    const {
        getState,
        nextPage,
        getCanNextPage,
        previousPage,
        getCanPreviousPage,
        setPageIndex,
        setPageSize,
        getPageCount,
        getHeaderGroups,
        getRowModel,
        getAllColumns,
        getSelectedRowModel,
    } = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnFiltersChange: setFilters,
        getRowId: row => {
            if (!row.id) {
                row = {
                    ...row,
                    id: uuid(),
                };
            }
            return row.id;
        },
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: getPageSize(level),
            },
            sorting: sortParams,
            globalFilter: filters,
        },
        defaultColumn: { enableSorting: false },
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
    });

    const rows = getRowModel().rows;
    const selectedFlatRows = getSelectedRowModel().flatRows;
    const tableColumns = getAllColumns();
    const {
        pageIndex, pageSize,
    } = getState().pagination;
    const sortBy = getState().sorting;
    const canNextPage = getCanNextPage();
    const canPreviousPage = getCanPreviousPage();
    const pageCount = getPageCount();

    useEffect(() => {
        onItemSelection && onItemSelection(selectedFlatRows.map((row: any) => row.original.id));
    }, [ onItemSelection, selectedFlatRows ]);

    useEffect(() => {
        const paginationArgs: PaginationArgs = {
            pageIndex,
            pageSize,
            pageCount,
            canNextPage,
            canPreviousPage,
            sortBy: (sortBy && sortBy[0]) ? sortBy[0].id : '',
            sortDirection: (sortBy && sortBy[0]) ? (sortBy[0].desc ? 'DESC' : 'ASC') : null,
            filters: filters as FiltersObj[],
        };
        if (onPagination) {
            onPagination(paginationArgs);
        }
    }, [ canNextPage, canPreviousPage, onPagination, pageCount, pageIndex, pageSize, sortBy, filters ]);

    const [ paginationChangeType, setPaginationChangeType ] = useState(PaginationChangeActionType.NONE);

    const gotoNextPage = (): void => {
        nextPage();
        setPaginationChangeType(PaginationChangeActionType.NEXT_PAGE);
    };

    const gotoPrevPage = (): void => {
        previousPage();
        setPaginationChangeType(PaginationChangeActionType.PREV_PAGE);
    };

    const onGoToPage = (toPageIndex: number): void => {
        setPaginationChangeType(toPageIndex === 0 ? PaginationChangeActionType.FIRST_PAGE : PaginationChangeActionType.LAST_PAGE);
        setPageIndex(toPageIndex);
    };

    const onPageSizeChange = (newPageSize: number): void => {
        localStorage.setItem(`${level}`, newPageSize.toString());
        setPageSize(localStorage.getItem(`${level}`) ? parseInt(localStorage.getItem(`${level}`) || '0') : newPageSize);
        setPaginationChangeType(PaginationChangeActionType.PAGE_SIZE);
    };
    const { t } = useTranslation();
    const hasActionColumn = !!contextMenuItems || !!icons || !!showRefresh;

    const sortCB = useCallback((ColumnId: string, desc?: boolean, isMulti?: boolean) => {
        const column = tableColumns.find(col => col.id === ColumnId);
        if (column) {
            column.toggleSorting(desc, isMulti);
        }
    }, [ tableColumns ]);

    const colWidth = isSelectAble ? getHeaderGroups()[0].headers.length + 2 : getHeaderGroups()[0].headers.length + 1;

    return (
        <MatTableContainer
            data-testid="table-container"
            className="selector-base-table"
            style={{ position: 'relative' }}
            data-cy="table-container">
            <StyledTable
                cellClicked={onTableCellClicked}
                size={size}
                aria-label={t('a11y_grid_content', { entityName })}>
                <TableHeaders
                    isfiltersVisible={isfiltersVisible}
                    isSelectAble={isSelectAble}
                    headerGroups={getHeaderGroups()}
                    gotoPage={setPageIndex}
                    hasActionColumn={hasActionColumn}
                    toggleSortBy={sortCB}
                    entityName={entityName}
                    hasRefreshButton={showRefresh}
                    refreshButtonClicked={refreshButtonClicked} />
                <MatTableBody>
                    <EmptyContent
                        empty={empty}
                        colspan={isSelectAble ? getHeaderGroups()[0].headers.length + 2 : getHeaderGroups()[0].headers.length + 1}
                        text={t('table_no_data')} />

                    {
                        isLoading ? (
                            Array(+pageSize).fill('')
                                .map((_, idx) => (
                                    <MatTableRow key={idx}>
                                        {Array(colWidth).fill('')
                                            .map((__, idxSk) => (
                                                <MatTableCell key={idxSk}>
                                                    <Skeleton
                                                        height="1.4rem"
                                                        variant="rectangular"
                                                        animation="wave" />
                                                </MatTableCell>
                                            ))}
                                    </MatTableRow>
                                ))
                        ) : rows.map((row, i) =>
                            (
                                <TableRow
                                    selectedFlatRows={selectedFlatRows}
                                    key={i}
                                    rowIndex={i}
                                    data-testid="table-row"
                                    data-cy="table-row"
                                    row={row}
                                    contextMenuItems={contextMenuItems}
                                    isSelectAble={isSelectAble}
                                    hasActionColumn={hasActionColumn}
                                    hasRefreshButton={showRefresh}
                                    icons={icons}
                                    onTableCellClicked={onTableCellClicked}
                                    tooltip={onRowHoverTooltip ?
                                        typeof onRowHoverTooltip === 'string' ? onRowHoverTooltip : onRowHoverTooltip(row)
                                        : undefined}
                                    keyColumnIndex={keyColumnIndex}
                                />
                            ),
                        )
                    }
                </MatTableBody>
            </StyledTable>
            {
                empty ? null : (<Pagination
                    nextPage={gotoNextPage}
                    previousPage={gotoPrevPage}
                    pageNum={pageIndex + 1}
                    recordsPerPage={pageSize as PaginationRecordsPerPage}
                    total={total}
                    isLoading={isLoading}
                    gotoPage={onGoToPage}
                    setPageSize={onPageSizeChange}
                    canNextPage={canNextPageProp}
                    paginationChangeActionType={paginationChangeType}
                    entityName={entityName}
                    level={level}
                />)
            }
            {
                (selectedFlatRows && selectedFlatRows.length && selectionItems && selectionItems.text) ? (
                    <StyledTableSelectionContainer>
                        <Button
                            aria-label={t('a11y_apply_ops_selected_content')}
                            color="secondary"
                            onClick={(event): void => {
                                event.stopPropagation();
                                selectionItems.click(selectedFlatRows);
                            }}
                            startIcon={<DeleteIcon />}
                            data-testid={`selection-${selectionItems.text}`}
                            data-cy={`selection-${selectionItems.text}`}
                        >
                            {t(selectionItems.text)}
                        </Button>
                    </StyledTableSelectionContainer>
                ) : null
            }
        </MatTableContainer >
    );
};

interface FiltersProps extends Conditional {
    headerGroup: HeaderGroupDefinition;
    gotoPage: any;
}

const Filters: React.FC<FiltersProps> = ({
    visible, headerGroup, gotoPage,
}) => {
    const { t } = useTranslation();
    return (
        visible ? (
            <MatTableRow>
                <td
                    aria-label={t('a11y_filter_control_label')}
                    colSpan={headerGroup.headers.length + 1}
                    style={{ padding: 0 }} >
                    <div style={{ display: 'flex' }}>
                        {headerGroup.headers.map((header, index) => {
                            const column = header.column;
                            const columnDef = column.columnDef;
                            const meta = columnDef.meta;
                            const filter = meta?.filter;
                            return (column.getCanFilter() && filter)
                                ? (
                                    <div
                                        className='filter'
                                        key={`filter_${index}`}>
                                        {

                                            flexRender(
                                                filter,
                                                {
                                                    column: {
                                                        setFilter: column.setFilterValue,
                                                        apiField: meta.apiField,
                                                        Header: columnDef.header,
                                                    },
                                                    gotoPage,
                                                },
                                            )
                                        }
                                    </div>
                                )
                                : null;
                        })}
                    </div>
                </td>
            </MatTableRow>
        ) : null
    );
};

const EmptyCell: React.FC<Conditional> = ({ visible }) => (visible ? <MatTableCell /> : null);

interface SortButtonParams extends Conditional {
    header: HeaderDefinition;
}

const SortButtons: React.FC<SortButtonParams> = ({
    visible, header,
}) => {
    const { t } = useTranslation();
    const sortDirection = header.column.getIsSorted();
    return visible ? (
        <span>
            <div
                className="sortBlock"
                data-testid={`sort-${header.id}`}
                data-cy={`sort-${header.id}`}>
                <WithVisibility visible={sortDirection === false}>
                    <ArrowDropUp
                        aria-hidden="false"
                        aria-label={t('a11y_sort_asc')}
                        fontSize="small" />
                    <ArrowDropDown
                        aria-hidden="false"
                        aria-label={t('a11y_sort_desc')}
                        fontSize="small" />
                </WithVisibility>
                <WithVisibility visible={sortDirection === 'asc'}>
                    <ArrowDropUp
                        aria-hidden="false"
                        aria-label={t('a11y_sort_asc')}
                        fontSize="small" />
                </WithVisibility>
                <WithVisibility visible={sortDirection === 'desc'}>
                    <ArrowDropDown
                        aria-hidden="false"
                        aria-label={t('a11y_sort_desc')}
                        fontSize="small" />
                </WithVisibility>
            </div>
        </span>
    ) : null;
};

const TableHeaders: React.FC<TableHeadersProps> = ({
    hasActionColumn, hasRefreshButton, headerGroups, isfiltersVisible, isSelectAble, toggleSortBy, gotoPage, entityName, refreshButtonClicked,
}) => {

    /**
     * If the table has an actions column or the refresh button, then the last header cell will be the refresh button
     * @param hasActionColumn Whether the table has an actions column
     * @param hasRefreshButton Whether the table has a refresh button
     * @returns The refresh button if the table has an actions column or the refresh button, otherwise null
     */
    const getLastHeaderContent = (hasActionColumn?: boolean, hasRefreshButton?: boolean) => {
        if (hasRefreshButton) {
            return <HeaderRefreshBtnContainer>
                <IconButton
                    name="refresh-button"
                    aria-label={t('a11y_refresh_grid', { entityName })}
                    data-testid="refreshIcon-btn"
                    data-cy="grid-refresh"
                    onClick={refreshButtonClicked}>
                    <RefreshIcon />
                </IconButton>
            </HeaderRefreshBtnContainer>;
        }

        return <EmptyCell visible={hasActionColumn} />;
    };

    return (
        <MatTableHead className="tableHead">
            <Filters
                visible={isfiltersVisible}
                headerGroup={headerGroups[0]}
                gotoPage={gotoPage} />
            {headerGroups.map((headerGroup: HeaderGroupDefinition, idx) => (
                <MatTableRow key={idx}>
                    <EmptyCell visible={isSelectAble} />
                    {headerGroup.headers.map((header: any, index: number) => (
                        <Hidden
                            key={'column_' + index}
                            xlDown={header.column.columnDef.xlDown}
                            lgDown={header.column.columnDef.lgDown}
                            mdDown={header.column.columnDef.mdDown}
                            smDown={header.column.columnDef.smDown}
                            xlUp={header.column.columnDef.xlUp}
                            lgUp={header.column.columnDef.lgUp}
                            mdUp={header.column.columnDef.mdUp}
                            smUp={header.column.columnDef.smUp}
                        >
                            <TableHeadCell
                                header={header}
                                toggleSortBy={toggleSortBy} />
                        </Hidden>
                    ))}
                    {
                        getLastHeaderContent(hasActionColumn, hasRefreshButton)
                    }
                </MatTableRow>
            ))}
        </MatTableHead>
    );
};
interface TableHeadCellProps {
    header: HeaderDefinition;
    toggleSortBy: (ColumnId: string, descending?: boolean, isMulti?: boolean) => void;
}
const TableHeadCell: React.FC<TableHeadCellProps> = ({
    header, toggleSortBy,
}) => {
    const cellRef = useRef<HTMLDivElement | null>(null);
    const cb = useCallback(bindKeyTo({
        ref: cellRef,
        cycle: true,
    }), [ cellRef ]);

    const isSortable = header.column.getCanSort();

    return (
        <MatTableCell
            onClick={(): void => {
                if (isSortable) {
                    toggleSortBy(header.id);
                }
            }}
            title=" "
            ref={cellRef}
            data-testid="table-cell-head"
            onKeyDown={cb}
            tabIndex={0}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
            }} >
                <ColumnTitle>
                    {String(header.column.columnDef.header)}
                </ColumnTitle>
                <SortButtons
                    visible={isSortable}
                    header={header} />
            </div>
        </MatTableCell>
    );
};
interface TableRowProps {
    row: Row<any>;
    contextMenuItems?: ContextMenuItem[];
    isSelectAble?: boolean;
    icons?: Icon[];
    hasActionColumn: boolean;
    hasRefreshButton: boolean;
    tooltip?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedFlatRows?: any;
    // todo: Sahil to fix this
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onTableCellClicked?(row: any, event: any): any;
    rowIndex: number;
    keyColumnIndex?: number;
}

const TableRow: React.FC<TableRowProps> = (
    {
        row,
        contextMenuItems,
        isSelectAble,
        hasActionColumn,
        icons,
        onTableCellClicked,
        selectedFlatRows,
        rowIndex,
        tooltip,
        keyColumnIndex,
    }) => {
    const { t } = useTranslation();
    const [ anchorEl, setAnchorEl ] = React.useState(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checked = !!selectedFlatRows.find((r: any) => r.id === row.id);
    const [ clicked, setClicked ] = React.useState(false);
    const ref = useRef<any>();
    const iconsToRender: Icon[] = icons ? [ ...icons ] : [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleMoreIconClick = (event: any): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleContextPopupClose = (): void => {
        setAnchorEl(null);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleOnTableCellClick = (event: any): void => {
        if (onTableCellClicked) {
            onTableCellClicked(row, event);
            setClicked(true);
        }
    };

    const keyInputHandler = useCallback(bindKeyTo({
        ref,
        traps: {
            [Key.Enter]: handleOnTableCellClick,
            [Key.Space]: handleOnTableCellClick,
            [Key.RightArrow]: () => {
                if (ref.current && filteredContextMenuItems && filteredContextMenuItems.length) {
                    ref.current.lastChild?.firstChild.focus();
                }
            },
        },
        cycle: true,
    }), [ ref ]);
    const filteredContextMenuItems = contextMenuItems ? contextMenuItems.filter((item: ContextMenuItem) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rowInfo: any = row.original;
        return !(rowInfo && rowInfo.blobType && rowInfo.blobType === 'DIRECTORY' && item.text === 'dataset_download_label');
    }) : [];
    const rowComponent = (
        <StyledTableRow
            isSelected={checked || clicked}
            className={onTableCellClicked ? 'cursor-pointer ' : ''}
            data-testid="table-row"
            data-cy="table-row"
            title=""
            tabIndex={rowIndex === 0 ? 0 : -1}
            ref={ref}
            onKeyDown={keyInputHandler}
        >
            {isSelectAble ?
                <MatTableCell>
                    <Checkbox
                        data-testid="table-selection-checkBox"
                        data-cy="table-selection-checkBox"
                        checked={checked}
                        value="primary"
                        color="secondary"
                        onChange={row.getToggleSelectedHandler()} />
                </MatTableCell> : null}
            {row.getVisibleCells().map((cell, index) => {
                const pointerClass = onTableCellClicked ? 'cursor-pointer' : '';
                const column: any = cell.column;
                return (
                    <Hidden
                        key={index}
                        xlDown={column.columnDef.xlDown}
                        lgDown={column.columnDef.lgDown}
                        mdDown={column.columnDef.mdDown}
                        smDown={column.columnDef.smDown}
                        xlUp={column.columnDef.xlUp}
                        lgUp={column.columnDef.lgUp}
                        mdUp={column.columnDef.mdUp}
                        smUp={column.columnDef.smUp}
                    >
                        {
                            keyColumnIndex !== undefined && keyColumnIndex === index ? (
                                <MatTableCell
                                    onClick={handleOnTableCellClick}
                                    data-testid="tableCell"
                                    component="th"
                                    scope="row"
                                    className={pointerClass}
                                    title="">
                                    <BodyText>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </BodyText>
                                </MatTableCell>
                            ) : (
                                <MatTableCell
                                    onClick={handleOnTableCellClick}
                                    data-testid="tableCell"
                                    className={pointerClass}
                                    title="">
                                    <BodyText>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </BodyText>
                                </MatTableCell>
                            )
                        }
                    </Hidden>
                );
            })}

            {hasActionColumn ? (
                <MatTableCell>
                    <StyledActionRow>
                        {
                            iconsToRender.map((item: Icon, index) => (
                                <GetIcon
                                    key={`${index}_${item.title}_${item.iconType}`}
                                    row={row}
                                    type={item}
                                />))
                        }
                        {(filteredContextMenuItems && filteredContextMenuItems.length) ?
                            (
                                <>
                                    <IconButton
                                        aria-label={t('a11y_more')}
                                        key={`more-btn-click-${rowIndex}`}
                                        data-testid="contextMenuOpen-btn"
                                        data-cy="edit-action-menu"
                                        size="small"
                                        onClick={handleMoreIconClick} >
                                        <More />
                                    </IconButton>
                                    <StyledContextPopupMenu
                                        autoFocus={false}
                                        style={{ marginLeft: '8px' }}
                                        id={`table-menu-${rowIndex}`}
                                        anchorEl={anchorEl}
                                        keepMounted
                                        open={Boolean(anchorEl)}
                                        anchorReference="anchorEl"
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        onClose={handleContextPopupClose}>
                                        {
                                            filteredContextMenuItems.map((item: ContextMenuItem, key: number) => {
                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                const rowInfo: any = row.original;
                                                if (item.type && item.type === 'deleteMLPackageVersionDto' && rowInfo &&
                                                (rowInfo.status === 'DEPLOYED' || rowInfo.status === 'VALIDATING' || rowInfo.status === 'DEPLOYING')) {
                                                    return (
                                                        <MenuItem
                                                            disabled
                                                            data-testid={`context-item-${item.text}`}
                                                            data-cy={`${item.text}`}
                                                            key={key}>
                                                            {t(item.text)}
                                                        </MenuItem>
                                                    );
                                                } else if (item.type && item.type === 'downloadMLPackageVersion' && rowInfo && (!rowInfo?.contentUri ||
                                                    (rowInfo.status === 'VALIDATING' || rowInfo.status === 'VALIDATION FAILED'))) {
                                                    // For docker image content url won't be there, disabling download button
                                                    return (<MenuItem
                                                        disabled
                                                        data-testid={`context-item-${item.text}`}
                                                        data-cy={`${item.text}`}
                                                        key={key}>
                                                        {t(item.text)}
                                                    </MenuItem>
                                                    );
                                                } else if (item.disable) {
                                                    return (
                                                        <MenuItem
                                                            data-testid={`context-item-${item.text}`}
                                                            data-cy={`${item.text}`}
                                                            key={key}
                                                            disabled={typeof item.disable === 'boolean' ? item.disable : item.disable(rowInfo)}
                                                            onClick={(event: React.MouseEvent<HTMLLIElement>): void => {
                                                                if (item.click) {
                                                                    item.click(event, row);
                                                                }
                                                                handleContextPopupClose();
                                                            }}
                                                        >
                                                            {t(item.text)}
                                                        </MenuItem>
                                                    );
                                                }
                                                return (
                                                    <MenuItem
                                                        data-testid={`context-item-${item.text}`}
                                                        data-cy={`${item.text}`}
                                                        key={key}
                                                        onClick={(event: React.MouseEvent<HTMLLIElement>): void => {
                                                            if (item.click) {
                                                                item.click(event, row);
                                                            }
                                                            handleContextPopupClose();
                                                        }}
                                                    >
                                                        {t(item.text)}
                                                    </MenuItem>
                                                );
                                            })
                                        }
                                    </StyledContextPopupMenu>
                                </>
                            ) : null}
                    </StyledActionRow>
                </MatTableCell>
            ) : null }
        </StyledTableRow>
    );
    if (tooltip) {
        return (
            <Tooltip title={tooltip}>
                {rowComponent}
            </Tooltip>);
    }
    return rowComponent;
};

interface GetIconProps {
    type: Icon;
    row: any;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GetIcon: React.FC<GetIconProps> = (props: any): ReactElement => {
    const {
        type, row,
    } = props;
    const additionalProps: any = { size: 'small' };
    const ref = useRef();
    const cb = useCallback(bindKeyTo({
        ref,
        cycle: true,
        cycleByLR: true,
    }), [ ref ]);
    if (ref) {
        additionalProps.buttonRef = ref;
        additionalProps.onKeyDown = cb;
        additionalProps.tabIndex = -1;
    }
    const SKILL_DELETE_ALLOWED_STATUS: string[] = [ 'AVAILABLE', 'FAILED' ];
    const { t } = useTranslation();

    switch (type.iconType) {
        case BaseTableIcons.DELETE:
            if (typeof type.isVisible === 'function' && !type.isVisible(row)) {
                return <></>;
            }
            if (row.original.status || row.original.blobName) {
                return (
                    <IconButton
                        aria-label={t('a11y_delete')}
                        {...additionalProps}
                        key={`delete-btn-${row.original.status}-${row.original.blobName}`}
                        data-testid="delete-btn"
                        data-cy="delete-button"
                        onClick={(event): void => {
                            if (type.click) {
                                type.click(event, row);
                            }
                        }}>
                        <Tooltip title={t('MLSKILL_DELETE_TOOLTIP')}>
                            <DeleteIcon />
                        </Tooltip>
                    </IconButton>
                );
            }
            return (
                <IconButton
                    aria-label={t('a11y_delete')}
                    {...additionalProps}
                    key={`delete-btn-disabled-${row.original.id || uuid()}`}
                    disabled
                    data-testid="delete-btn"
                    data-cy="delete-button">
                    <Tooltip title={t('MLSKILL_DELETE_TOOLTIP')}>
                        <DeleteIcon />
                    </Tooltip>
                </IconButton>
            );

        case BaseTableIcons.CANCEL:
            if (typeof type.isVisible === 'function' && !type.isVisible(row)) {
                return <></>;
            }
            if (row.original.status || row.original.blobName) {
                return (
                    <IconButton
                        aria-label={t('a11y_cancel')}
                        {...additionalProps}
                        key={`cancel-btn-${row.original.status}-${row.original.blobName}`}
                        data-testid="cancel-btn"
                        data-cy="cancel-button"
                        onClick={(event): void => {
                            if (type.click) {
                                type.click(event, row);
                            }
                        }}>
                        <Tooltip title={t('MLSKILL_CANCEL_TOOLTIP')}>
                            <StopCircle />
                        </Tooltip>
                    </IconButton>
                );
            }
            return (
                <IconButton
                    aria-label={t('a11y_cancel')}
                    {...additionalProps}
                    key={`cancel-btn-disabled-${row.original.id || uuid()}`}
                    disabled
                    data-testid="cancel-btn"
                    data-cy="cancel-button">
                    <Tooltip title={t('MLSKILL_CANCEL_TOOLTIP')}>
                        <StopCircle />
                    </Tooltip>
                </IconButton>
            );

        case BaseTableIcons.INFO:
            return (
                <IconButton
                    aria-label={t('a11y_info')}
                    {...additionalProps}
                    key={`info-btn-click-${row.original.id || uuid()}`}
                    data-testid="info-btn"
                    data-cy="info-button"
                    onClick={(event): void => {
                        if (type.click) {
                            type.click(event, row);
                        }
                    }} >
                    <InfoIcon />
                </IconButton>
            );

        case BaseTableIcons.DOWNLOAD:

            /* Don't show download button when blobType is directory */
            if (row.original.blobType && row.original.blobType === 'DIRECTORY') {
                return <span />;
            }

            return (
                <IconButton
                    aria-label={t('a11y_download')}
                    {...additionalProps}
                    key={`download-btn-click-${row.original.id || uuid()}`}
                    data-testid="download-btn"
                    data-cy="download-button"
                    onClick={(event): void => {
                        if (type.click) {
                            type.click(event, row);
                        }
                    }} >
                    <GetAppIcon />
                </IconButton>
            );

        case BaseTableIcons.MORE:

            return (
                <IconButton
                    {...additionalProps}
                    aria-label={t('a11y_more')}
                    key={`more-btn-click-${row.original.id || uuid()}`}
                    data-testid="contextMenuOpen-btn"
                    data-cy="edit-action-menu"
                    onClick={(event): void => {
                        if (type.click) {
                            type.click(event, row);
                        }
                    }} >
                    <More />
                </IconButton>
            );

        case BaseTableIcons.SWAPVERT:
            if (window.currentVersion === (row.original.version + '.' + row.original.trainingVersion)
        || (SKILL_DELETE_ALLOWED_STATUS.indexOf(window.currentStatus ? window.currentStatus : '') === -1)) {
                return (
                    <IconButton
                        aria-label={t('a11y_update')}
                        {...additionalProps}
                        key={`swap-vert-btn-click-${row.original.id || uuid()}`}
                        disabled
                        data-testid="swapvert-btn"
                        data-cy="swapvert-button" >
                        <SwapVertIcon />
                    </IconButton>
                );
            }
            return (
                <IconButton
                    {...additionalProps}
                    aria-label={t('a11y_update')}
                    key={`swap-vert-2-btn-click-${row.original.id || uuid()}`}
                    data-testid="swapvert-btn"
                    data-cy="swapvert-button"
                    onClick={(event): void => {
                        if (type.click) {
                            type.click(event, row);
                        }
                    }} >
                    <SwapVertIcon />
                </IconButton>
            );

    }
    return <span />;
};

export default CustomTable;
